import { FC, useState } from 'react';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { DailySerie, VisaoGeralMes } from '@/types/dashboards';
import { mesDailyAberto, pontosDaily, type PontoDaily } from '@/utils/equiplexMaquinas';
import { fmtCompacto, fmtDiaMes, fmtPct, fmtUn } from '@/utils/painelFormat';
import { escalaDoDia, mostrarIndices, ultimoPonto, valoresDe } from '@/utils/rotulosGrafico';
import { ALTURA_GRAFICO_REPETIDO, tickEixo, type CoresPainel } from '../chartTheme';
import Legenda from '../Legenda';
import PainelCard from '../PainelCard';
import { rotuloDoPonto } from '../rotuloBarra';
import TooltipBox from '../TooltipBox';
import ColunasDoDia from './ColunasDoDia';
import MetasMaquinas from './MetasMaquinas';

type Modo = 'acumulado' | 'diario';

interface Props {
  linhas: DailySerie[];
  mes: VisaoGeralMes;
  /** Último dia com produção reportada: dia sem OP até ele está fechado, depois é futuro. */
  corte: string;
  cores: CoresPainel;
  /** Meta ajustada: a aba busca o payload de novo. */
  onMetasSalvas?: () => void;
}

const TRACEJADO = '5 4';
const MARGEM = { top: 24, right: 6, bottom: 0, left: 0 };

const eixoX = (cores: CoresPainel) => ({
  dataKey: 'data',
  tickFormatter: (d: string) => d.slice(8),
  interval: 'preserveStartEnd' as const,
  minTickGap: 4,
  tick: tickEixo(cores),
  axisLine: false,
  tickLine: false,
});

const eixoY = (cores: CoresPainel) => ({
  width: 60,
  tickFormatter: fmtCompacto,
  tick: tickEixo(cores),
  axisLine: false,
  tickLine: false,
});

const usaMeta = (serie: DailySerie) => serie.programacao === 'meta';

function linhasDoTooltip(p: PontoDaily, modo: Modo, cores: CoresPainel, meta: boolean) {
  if (modo === 'diario') {
    return [
      { rotulo: meta ? 'Meta' : 'Programado', valor: fmtUn(p.programado), cor: cores.programado },
      { rotulo: 'Produzido', valor: fmtUn(p.produzido), cor: cores.produzido },
    ];
  }
  return [
    { rotulo: meta ? 'Meta acumulada' : 'Programação acumulada', valor: fmtUn(p.programacaoAcum), cor: cores.programado },
    ...(p.realizadoAcum !== null ? [{ rotulo: 'Produzido acumulado', valor: fmtUn(p.realizadoAcum), cor: cores.produzido }] : []),
    ...(p.projecaoAcum !== null && p.realizadoAcum === null ? [{ rotulo: 'Projeção', valor: fmtUn(p.projecaoAcum), cor: cores.produzido }] : []),
  ];
}

const rotuloAbertas = (n: number) => (n === 1 ? '1 ordem aberta' : n > 1 ? `${n} ordens abertas` : 'ordens abertas');

function tooltip(cores: CoresPainel, modo: Modo, meta: boolean) {
  const Conteudo = ({ active, payload }: TooltipContentProps) => {
    const p = active && payload?.[0] ? (payload[0].payload as PontoDaily) : null;
    if (!p) return null;
    const aberto = p.situacao === 'aberto' ? ` · ${rotuloAbertas(p.abertas)}` : p.situacao === 'futuro' ? ' · ainda por vir' : '';
    return <TooltipBox titulo={`${fmtDiaMes(p.data)}${aberto}`} linhas={linhasDoTooltip(p, modo, cores, meta)} />;
  };
  return Conteudo;
}

/** O número que a reunião lê: projeção do mês contra a programação (ou como o mês fechou). */
const Resumo: FC<{ serie: DailySerie; mesAberto: boolean }> = ({ serie, mesAberto }) => {
  if (!serie.programado_mes) return <p className="text-xs text-content-muted">Nenhuma OP desta linha no mês.</p>;
  const meta = usaMeta(serie);
  const base = meta ? 'da meta do período' : 'programados';
  return (
    <p className="text-xs text-content-secondary" data-testid={`daily-resumo-${serie.linha}`}>
      {mesAberto ? 'Projeção' : 'Fechou em'}{' '}
      <span className="font-semibold text-content-primary">{fmtUn(mesAberto ? serie.projecao : serie.produzido_fechado)}</span> de{' '}
      {fmtUn(serie.programado_mes)} {base} ({fmtPct(serie.pct_projecao)})
      {meta && serie.meta_dia ? <> · meta {fmtUn(serie.meta_dia)}/dia</> : null}
      {mesAberto && serie.aderencia_fechado !== null && (
        <>
          {' '}
          · dias com as ordens fechadas: {fmtPct(serie.aderencia_fechado)} {meta ? 'da meta' : 'do programado'}
        </>
      )}
    </p>
  );
};

const Rotulo: FC<{ texto: string; complemento?: string }> = ({ texto, complemento }) => (
  <p className="mt-3 text-xs font-medium uppercase tracking-wide text-content-muted">
    {texto}
    {complemento && <span className="font-normal normal-case tracking-normal"> · {complemento}</span>}
  </p>
);

const PorDia: FC<{ pontos: PontoDaily[]; cores: CoresPainel; meta: boolean; formatar: (v: number) => string }> = ({
  pontos,
  cores,
  meta,
  formatar,
}) => (
  <ColunasDoDia
    pontos={pontos.map((p) => ({ ...p, esmaecido: p.situacao === 'aberto' || p.situacao === 'futuro' }))}
    cores={cores}
    tooltip={tooltip(cores, 'diario', meta)}
    formatar={formatar}
  />
);

/** "prog. 2,4 mi" no fim da programação; o produzido, no último dia fechado. */
function rotulosAcumulado(pontos: PontoDaily[], cores: CoresPainel, meta: boolean) {
  const fimProgramacao = mostrarIndices(ultimoPonto(valoresDe(pontos, 'programacaoAcum')), false);
  const fimRealizado = ultimoPonto(valoresDe(pontos, 'realizadoAcum'));
  // Mês fechado: o realizado termina no mesmo dia da programação; o número dele desce para não encavalar.
  const noUltimoDia = fimRealizado.has(pontos.length - 1);
  return {
    programacao: rotuloDoPonto((v) => `${meta ? 'meta' : 'prog.'} ${fmtCompacto(v)}`, cores.texto, fimProgramacao, { ancora: 'end' }),
    realizado: rotuloDoPonto(fmtCompacto, cores.texto, mostrarIndices(fimRealizado, false), {
      ancora: noUltimoDia ? 'end' : 'middle',
      abaixo: noUltimoDia,
    }),
  };
}

const Acumulado: FC<{ pontos: PontoDaily[]; cores: CoresPainel; meta: boolean }> = ({ pontos, cores, meta }) => {
  const rotulos = rotulosAcumulado(pontos, cores, meta);
  return (
    <div style={{ width: '100%', height: ALTURA_GRAFICO_REPETIDO }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={pontos} margin={MARGEM}>
          <CartesianGrid vertical={false} stroke={cores.grade} />
          <XAxis {...eixoX(cores)} />
          <YAxis {...eixoY(cores)} />
          <Tooltip content={tooltip(cores, 'acumulado', meta)} cursor={{ stroke: cores.eixo, strokeDasharray: '3 3' }} />
          <Line
            dataKey="programacaoAcum"
            name="Programação"
            stroke={cores.programado}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          >
            <LabelList dataKey="programacaoAcum" content={rotulos.programacao} />
          </Line>
          <Line
            dataKey="realizadoAcum"
            name="Produzido"
            stroke={cores.produzido}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          >
            <LabelList dataKey="realizadoAcum" content={rotulos.realizado} />
          </Line>
          <Line
            dataKey="projecaoAcum"
            name="Projeção"
            stroke={cores.produzido}
            strokeWidth={2}
            strokeDasharray={TRACEJADO}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Os dois gráficos da linha, um embaixo do outro: o dia (colunas) e o mês acumulado (linhas). */
const Graficos: FC<{ serie: DailySerie; pontos: PontoDaily[]; cores: CoresPainel }> = ({ serie, pontos, cores }) => {
  const meta = usaMeta(serie);
  const escala = escalaDoDia(pontos.map((p) => p.produzido));
  return (
    <>
      <Rotulo texto="Por dia" complemento={`números do produzido em ${escala.unidade}`} />
      <PorDia pontos={pontos} cores={cores} meta={meta} formatar={escala.formatar} />
      <Rotulo texto="Acumulado no mês" />
      <Acumulado pontos={pontos} cores={cores} meta={meta} />
      <span className="sr-only">{`${serie.rotulo}: ${fmtUn(serie.produzido_fechado)} produzidos de ${fmtUn(serie.programado_mes)} programados`}</span>
    </>
  );
};

const CartaoLinha: FC<{ serie: DailySerie; mes: VisaoGeralMes; corte: string; mesAberto: boolean; cores: CoresPainel }> = ({
  serie,
  mes,
  corte,
  mesAberto,
  cores,
}) => (
  <div className="min-w-0 rounded-lg border border-theme-border p-3" data-testid={`daily-${serie.linha}`}>
    <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-2">
      <h3 className="text-sm font-semibold text-content-primary">{serie.rotulo}</h3>
      <span className="text-2xs text-content-muted">
        {serie.descricao}
        {usaMeta(serie) ? ' · programação = meta' : ' · programação = OPs'}
      </span>
    </div>
    <Resumo serie={serie} mesAberto={mesAberto} />
    {serie.programado_mes > 0 && <Graficos serie={serie} pontos={pontosDaily(serie, mes, corte)} cores={cores} />}
  </div>
);

function legenda(cores: CoresPainel, mesAberto: boolean) {
  return [
    { rotulo: 'Programação (meta de cada máquina por dia): contorno tracejado no dia, linha no acumulado', cor: cores.programado, tracejado: true },
    { rotulo: 'Produzido (dias com as ordens fechadas)', cor: cores.produzido },
    ...(mesAberto
      ? [{ rotulo: 'Projeção: produzido dos dias fechados + programado dos dias com ordem aberta', cor: cores.produzido, tracejado: true }]
      : []),
  ];
}

const BotaoMetas: FC<{ aberto: boolean; onClick: () => void }> = ({ aberto, onClick }) => (
  <button
    type="button"
    aria-expanded={aberto}
    onClick={onClick}
    className="print-hide inline-flex items-center gap-1 rounded-lg border border-theme-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:text-content-primary"
  >
    <span aria-hidden className="material-symbols-outlined text-[16px] leading-none">tune</span>
    Metas das máquinas
  </button>
);

/**
 * A daily da reunião (o gráfico do Attila, planilha "Projeção Produção"): por linha — sopro
 * consolidado, 100 e 250 ml, grande e pequeno volume —, produzido × programado por dia e o
 * acumulado do mês com a projeção contra a programação. Dois gráficos empilhados em vez de um com
 * dois eixos. A programação é a meta de cada máquina por dia, ajustada aqui mesmo; o dia fecha
 * quando as ordens dele fecham. Uma linha por vez, na largura toda, com o número de cada dia
 * (26/10/2026).
 */
const DailyLinhas: FC<Props> = ({ linhas, mes, corte, cores, onMetasSalvas }) => {
  const [metasAbertas, setMetasAbertas] = useState(false);
  if (linhas.length === 0) return null;
  const mesAberto = mesDailyAberto(linhas, mes, corte);
  const temMeta = linhas.some((l) => l.usa_meta);
  return (
    <PainelCard
      testId="maq-daily"
      titulo="Daily: produção × programação por linha"
      subtitulo={`${mes.label}. Programação = meta de cada máquina por dia, somada no período. ${
        mesAberto
          ? 'Dia fechado = todas as ordens do dia encerradas; a projeção soma o produzido desses dias ao programado dos dias com ordem aberta ou por vir.'
          : 'Mês fechado.'
      }`}
      acoes={temMeta ? <BotaoMetas aberto={metasAbertas} onClick={() => setMetasAbertas((v) => !v)} /> : undefined}
    >
      {metasAbertas && <MetasMaquinas onSalva={onMetasSalvas} />}
      <Legenda itens={legenda(cores, mesAberto)} />
      <div className="flex flex-col gap-3">
        {linhas.map((serie) => (
          <CartaoLinha key={serie.linha} serie={serie} mes={mes} corte={corte} mesAberto={mesAberto} cores={cores} />
        ))}
      </div>
    </PainelCard>
  );
};

export default DailyLinhas;
